/**
 * Common empty state component
 */

import { Inbox } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'destructive' | 'secondary' | 'ghost' | 'link';
}

interface EmptyStateProps {
  title?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: EmptyStateAction;
  actions?: EmptyStateAction[];
  searchQuery?: string;
}

export function EmptyState({
  title = 'No items found',
  description = 'There are no items to display at this time.',
  icon: Icon = Inbox,
  action,
  actions,
  searchQuery,
}: EmptyStateProps) {
  const displayTitle = searchQuery
    ? `No results for "${searchQuery}"`
    : title;

  const displayDescription = searchQuery
    ? 'Try adjusting your search terms or filters.'
    : description;

  // Support both single action and multiple actions
  const actionButtons = actions || (action ? [action] : []);

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center p-12">
        <div className="rounded-full bg-muted p-4 mb-4">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
        <CardHeader className="text-center">
          <CardTitle>{displayTitle}</CardTitle>
          <CardDescription>{displayDescription}</CardDescription>
        </CardHeader>
        {actionButtons.length > 0 && (
          <div className="flex gap-3 mt-4">
            {actionButtons.map((act, index) => (
              <Button
                key={index}
                onClick={act.onClick}
                variant={act.variant || (index === 0 ? 'default' : 'outline')}
              >
                {act.label}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}






